import { find, forEach } from 'lodash'
import * as yup from 'yup'

import {
  CombosAndUpgrades,
  CountryContent,
  FestivalDay,
  Gradient,
  TicketCategory,
  TicketPayload,
  TicketTranslations,
  UsableExtras,
} from '../../../common/models'
import { TicketExtras } from '../../../common/models/ticket'
import { FinancialSettingsCategory } from '@/common/models/settings'

const paymentOptions = [
  {
    name: 'BOTH',
    displayValue: 'BOTH',
  },
  {
    name: 'FULL_PRICE',
    displayValue: 'FULL_PRICE',
  },
  {
    name: 'MONTHLY',
    displayValue: 'MONTHLY',
  },
]

export interface IValues {
  content?: TicketTranslations[]
  category: string
  color: string
  ordinal: number
  publishedDate?: string
  published: boolean
  externalSell: boolean
  manualSoldOut?: boolean
  dayTicket: string
  discount?: number | null
  discountUntil?: string | null
  price: number
  stock?: number
  unlimited?: boolean | null
  maxPerOrderUnlimited?: boolean | null
  groupTickets?: CombosAndUpgrades[]
  upgradeTickets?: CombosAndUpgrades[]
  addons?: {
    addonId?: string
    name: string
    description: string
    price: number
    startTimestamp: number
    endTimestamp: number
    countryDependentPrice: boolean
    countryPrices: CountryContent[]
  }[]
  intownCheckInCode?: string | null

  unpublishedDate?: string
  soldOutDate?: string
  // payment config
  maxPerOrder?: number | null
  canBeSoldTillDateTimestamp?: string | null
  paymentOption?: 'BOTH' | 'FULL_PRICE' | 'MONTHLY'
  installment?: {
    installmentEndDate?: string | null
    nrMaxPayments?: number | null
    firstInstallmentPercentage?: number | null
    firstInstallmentValue?: number | null
    feePercentage?: number | null
    feeValue?: number | null
  } | null

  bonus?: {
    bonusPercentage?: number | null
    bonusValue?: number | null
    bonusExpiryDate?: string | null
    bonusAvailableDays?: number | null
  } | null
  dubaiArea?: string
  dubaiPriceTypeCode?: string
  dubaiCodePerTicket?: boolean
  dubaiCodePerContainedTicket?: boolean
  dubaiCompensation?: boolean
  dubaiIntegration: boolean
  corporate: boolean
  supportScanning: boolean
  backstageSync: boolean
  checkinEnabled: boolean
  backstageProductId?: string | null
  alcatrazIntegratable: boolean
  alcatrazFacilityCode?: number | null
  onlyForReferral: boolean
  transferTax?: number | null
  taxCategory?: FinancialSettingsCategory
  campaign?: {
    id: string
    name: string
  } | null
}

export const ticketSchema = yup.object().shape(
  {
    content: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          locale: yup.string().required(),
          name: yup.string().required(),
          description: yup.string().required(),
        }),
      ),
    category: yup.string().required(),
    backstageProductId: yup.string().nullable().optional(),
    backstageSync: yup.boolean().required().default(false),
    checkinEnabled: yup.boolean().required().default(false),
    alcatrazIntegratable: yup.boolean().required().default(false),
    alcatrazFacilityCode: yup
      .number()
      .nullable()
      .optional()
      .transform((value) => (Number.isNaN(value) ? null : value)),
    color: yup.string().required(),
    ordinal: yup.number().required(),
    stock: yup.number().when('unlimited', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) =>
        schema
          .nullable()
          .optional()
          .transform((val) => (!isNaN(val) ? val : null)),
      otherwise: (schema) =>
        schema
          .optional()
          .nullable()
          .transform((val) => (!isNaN(val) ? val : null)),
    }),
    unlimited: yup.boolean().nullable().optional(),
    maxPerOrderUnlimited: yup.boolean().nullable().optional(),
    maxPerOrder: yup.number().when('maxPerOrderUnlimited', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) =>
        schema
          .nullable()
          .optional()
          .transform((val) => (!isNaN(val) ? val : null)),
      otherwise: (schema) =>
        schema
          .optional()
          .nullable()
          .transform((val) => (!isNaN(val) ? val : null)),
    }),
    addons: yup
      .array()
      .of(
        yup.object().shape({
          addonId: yup.string().required(),
          name: yup.string().required(),
          description: yup.string().required(),
          price: yup.number().required(),
          startTimestamp: yup.number().required(),
          endTimestamp: yup.number().required(),
          countryDependentPrice: yup.boolean().required().default(false),
          countryPrices: yup
            .array()
            .of(
              yup.object().shape({
                countryId: yup.string().required(),
                countryName: yup.string().required(),
                price: yup.number().required(),
              }),
            )
            .required(),
        }),
      )
      .optional(),

    dayTicket: yup.string().required(),

    paymentOption: yup
      .string()
      .oneOf(['BOTH', 'FULL_PRICE', 'MONTHLY'])
      .required(),
    price: yup.number().required(),
    discount: yup
      .number()
      .nullable()
      .optional()
      .transform((value) => (Number.isNaN(value) ? null : value)),
    discountUntil: yup.string().nullable().optional(),
    groupTickets: yup.array().of(
      yup.object().shape({
        festivalEdition: yup
          .object()
          .shape({
            id: yup.string().required(),
            name: yup.string().required(),
          })
          .required(),
        category: yup
          .object()
          .shape({
            id: yup.string().required(),
            name: yup.string().required(),
          })
          .required(),
        ticket: yup
          .object()
          .shape({
            id: yup.string().required(),
            name: yup.string().required(),
          })
          .required(),
        viewPrice: yup.number().optional(),
      }),
    ),
    upgradeTickets: yup
      .array()
      .min(0)
      .of(
        yup.object().shape({
          festivalEdition: yup
            .object()
            .shape({
              id: yup.string().required(),
              name: yup.string().required(),
            })
            .required(),
          category: yup.object().shape({
            id: yup.string().required(),
            name: yup.string().required(),
          }),
          ticket: yup.object().shape({
            id: yup.string().required(),
            name: yup.string().required(),
          }),
          viewPrice: yup.number().optional(),
        }),
      ),
    publishedDate: yup.string(),
    unpublishedDate: yup.string(),
    soldOutDate: yup.string(),
    published: yup.boolean().required().default(false),
    externalSell: yup.boolean().required().default(false),
    manualSoldOut: yup.boolean().optional(),
    canBeSoldTillDateTimestamp: yup
      .string()
      .nullable()
      .optional()
      .transform((date) => (!isNaN(date) ? date : null)),
    installment: yup
      .object()
      .shape({
        installmentEndDate: yup
          .string()
          .test('installmentEndDate', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const nrMaxPayments = from?.[1]?.value?.installment?.nrMaxPayments
            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return originalValue || Number(nrMaxPayments)
          }),
        nrMaxPayments: yup
          .number()
          .test('nrMaxPayments', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const installmentEndDate =
              from?.[1]?.value?.installment?.installmentEndDate
            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return installmentEndDate || Number(originalValue)
          }),
        firstInstallmentPercentage: yup
          .number()
          .test('firstInstallmentPercentage', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const firstInstallmentValue =
              from?.[1]?.value?.installment?.firstInstallmentValue

            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return originalValue || firstInstallmentValue
          })
          .transform((value) => (value ? Number(value) : 0)),
        firstInstallmentValue: yup
          .number()
          .test('firstInstallmentValue', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const firstInstallmentPercentage =
              from?.[1]?.value?.installment?.firstInstallmentPercentage
            if (paymentOption === 'FULL_PRICE') {
              return true
            }

            return originalValue || firstInstallmentPercentage > 0
          })
          .transform((value) => (value ? Number(value) : 0)),
        feePercentage: yup
          .number()
          .test('feePercentage', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const feeValue = from?.[1]?.value?.installment?.feeValue
            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return originalValue || feeValue
          })
          .transform((value) => (value ? Number(value) : 0)),
        feeValue: yup
          .number()
          .test('feeValue', 'required', function () {
            const { from, originalValue } = this
            const paymentOption = from?.[1]?.value.paymentOption
            const feePercentage = from?.[1]?.value?.installment?.feePercentage
            if (paymentOption === 'FULL_PRICE') {
              return true
            }
            return originalValue || feePercentage
          })
          .transform((value) => (value ? Number(value) : 0)),
      })
      .when('paymentOption', {
        is: (val: 'MONTHLY' | 'FULL_PRICE' | 'BOTH') => val === 'FULL_PRICE',
        then: (schema) => schema.nullable().optional(),
        otherwise: (schema) => schema.required(),
      }),
    bonus: yup
      .object()
      .shape({
        bonusPercentage: yup
          .number()
          .nullable()
          .optional()
          .transform((value) => (Number.isNaN(value) ? null : value)),
        bonusValue: yup
          .number()
          .nullable()
          .optional()
          .transform((value) => (Number.isNaN(value) ? null : value)),
        bonusExpiryDate: yup
          .string()
          .nullable()
          .optional()
          .transform((date) => (isNaN(date) ? date : null)),
        bonusAvailableDays: yup
          .number()
          .nullable()
          .optional()
          .transform((value) => (Number.isNaN(value) ? null : value)),
      })
      .nullable()
      .optional(),
    intownCheckInCode: yup.string().nullable().optional(),
    dubaiArea: yup.string().when('dubaiIntegration', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.optional(),
      otherwise: () => yup.string().optional(),
    }),
    dubaiPriceTypeCode: yup.string().when('dubaiIntegration', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),
    dubaiCodePerTicket: yup.boolean().when('dubaiIntegration', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required().default(false),
      otherwise: (schema) => schema.optional(),
    }),
    dubaiCodePerContainedTicket: yup.boolean().when('dubaiIntegration', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required().default(false),
      otherwise: (schema) => schema.optional(),
    }),
    dubaiCompensation: yup.boolean().when('dubaiIntegration', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.required().default(false),
      otherwise: (schema) => schema.optional(),
    }),
    dubaiIntegration: yup.boolean().required().default(false),
    corporate: yup.boolean().required().default(false),
    supportScanning: yup.boolean().required().default(false),
    transferTax: yup.number().optional().nullable(),
    onlyForReferral: yup.boolean().required().default(false),
    taxCategory: yup.mixed<FinancialSettingsCategory>().optional(),
    campaign: yup
      .object()
      .shape({
        id: yup.string().required(),
        name: yup.string().required(),
      })
      .nullable()
      .optional(),
  },
  [
    ['buyerCashbackPercentage', 'buyerCashbackValue'],
    ['buyerCashbackExpiryDate', 'buyerCashbackAvailableDays'],
    ['referrerCashbackPercentage', 'referrerCashbackValue'],
    ['referrerCashbackExpiryDate', 'referrerCashbackAvailableDays'],
    ['bonusExpiryDate', 'bonusAvailableDays'],
    ['bonusPercentage', 'bonusValue'],
    ['installmentEndDate', 'nrMaxPayments'],
    ['firstInstallmentPercentage', 'firstInstallmentValue'],
    ['feePercentage', 'feeValue'],
    ['stock', 'unlimited'],
    ['maxPerOrder', 'maxPerOrderUnlimited'],
  ],
)

export const ticketDefaultValues: Partial<IValues> = {
  addons: [],
  paymentOption: 'BOTH',
  unlimited: true,
  maxPerOrderUnlimited: true,
  dubaiIntegration: false,
  corporate: false,
  supportScanning: false,
  onlyForReferral: false,
  externalSell: false,
  transferTax: null,
  installment: {
    nrMaxPayments: 0,
  },
  backstageSync: false,
  published: false,
  checkinEnabled: false,
  alcatrazIntegratable: false,
  alcatrazFacilityCode: null,
  campaign: null,
}

interface BuildPayloadOptions {
  category?: TicketCategory
  colors: Gradient[]
  festivalDays: FestivalDay[]
  allAddons: UsableExtras[]
  activeAddons: TicketExtras[]
}

const buildTicketExtras = (
  values: IValues,
  allAddons: UsableExtras[],
  activeAddons: TicketExtras[],
): TicketExtras[] => {
  const extras: TicketExtras[] = []

  forEach(values.addons, (addon, index) => {
    const temp = find(allAddons, (item) => item.id === addon.addonId)
    if (!temp) return

    extras.push({
      id: activeAddons?.[index]?.id,
      extrasId: values.addons?.[index]?.addonId || temp.id,
      name:
        values?.addons?.[index]?.name ||
        find(temp.translations, (translation) => translation.locale === 'en')
          ?.name ||
        '',
      description:
        values?.addons?.[index]?.description ||
        find(temp.translations, (translation) => translation.locale === 'en')
          ?.description ||
        '',
      price: values?.addons?.[index]?.price || temp.defaultPrice,
      startTimestamp:
        values?.addons?.[index]?.startTimestamp || temp.startTimestamp,
      endTimestamp: values?.addons?.[index]?.endTimestamp || temp.endTimestamp,
      countryDependentPrice: temp.countryDependentPrice,
      countryPrices:
        values?.addons?.[index]?.countryPrices || temp.countryPrices,
    })
  })

  return extras
}

export const buildTicketPayload = (
  values: IValues,
  {
    category,
    colors,
    festivalDays,
    allAddons,
    activeAddons,
  }: BuildPayloadOptions,
): TicketPayload => {
  const extras = buildTicketExtras(values, allAddons, activeAddons)
  const data: TicketPayload = {
    translations: values.content,
    ordinal: values.ordinal,
    ticketCategory: category,
    color: find(colors, (color) => color.name === values.color),
    stock:
      values.unlimited || (values.stock && values.stock < 0)
        ? -1
        : values.stock,
    unlimited: values.unlimited || false,
    extras,
    festivalDay: find(festivalDays, (days) => days.id === values.dayTicket),
    upgrades: values.upgradeTickets,
    combos: values.groupTickets,
    price: values.price,
    discountPrice: values.discount,
    backstageProductId: values.backstageProductId,
    backstageSync: values.backstageSync,
    checkinEnabled: values.checkinEnabled,
    alcatrazIntegratable: values.alcatrazIntegratable,
    alcatrazFacilityCode: values.alcatrazFacilityCode,
    soldOutTimestamp: new Date(values?.soldOutDate || '').valueOf(),
    discountPriceEndTimestamp: values.discountUntil
      ? new Date(values.discountUntil)?.getTime()
      : null,
    publishTimestamp: new Date(values?.publishedDate || '').valueOf(),
    unpublishTimestamp: new Date(values?.unpublishedDate || '').valueOf(),
    published: values.published,
    externalSell: values.externalSell,
    corporate: values.corporate,
    supportScanning: values.supportScanning,
    transferTax: values.transferTax || 0,
    onlyForReferral: values.onlyForReferral,
    paymentsConfig: {},
    dubaiArea: values.dubaiArea,
    dubaiPriceTypeCode: values.dubaiPriceTypeCode,
    codePerTicket: values.dubaiCodePerTicket,
    codePerContainedTicket: values.dubaiCodePerContainedTicket,
    registerCompFree: values.dubaiCompensation,
    intownTicketId: values.intownCheckInCode,
    taxCategory: values.taxCategory,
    campaign: values.campaign,
  }

  if (
    data.paymentsConfig &&
    (values?.maxPerOrder || values?.maxPerOrderUnlimited)
  ) {
    data.paymentsConfig.maxPerOrder = values?.maxPerOrderUnlimited
      ? -1
      : values.maxPerOrder
  }

  if (data.paymentsConfig && values?.canBeSoldTillDateTimestamp) {
    data.paymentsConfig.sellingCutOffTimestamp = new Date(
      values.canBeSoldTillDateTimestamp,
    ).valueOf()
  }
  if (data.paymentsConfig && values?.paymentOption) {
    data.paymentsConfig.paymentOption =
      find(paymentOptions, (option) => option.name === values?.paymentOption) ||
      paymentOptions[0]
  }
  if (data.paymentsConfig && values?.installment) {
    data.paymentsConfig.installmentConfig = {
      installmentEndDate:
        !values?.installment?.nrMaxPayments &&
        values?.installment.installmentEndDate
          ? new Date(values?.installment.installmentEndDate)?.valueOf()
          : null,
      nrMaxPayments:
        values?.installment.nrMaxPayments !== 0
          ? values?.installment.nrMaxPayments
          : null,
      firstInstallmentPercentage:
        values?.installment.firstInstallmentPercentage !== 0
          ? values?.installment.firstInstallmentPercentage
          : null,
      firstInstallmentValue:
        values?.installment.firstInstallmentValue !== 0
          ? values?.installment.firstInstallmentValue
          : null,
      feePercentage:
        values?.installment.feePercentage !== 0
          ? values?.installment.feePercentage
          : null,
      feeValue:
        values?.installment.feeValue !== 0
          ? values?.installment.feeValue
          : null,
    }
  }
  if (data.paymentsConfig && values?.bonus) {
    data.paymentsConfig.bonusConfig = {
      bonusPercentage:
        values?.bonus.bonusPercentage !== 0
          ? values?.bonus.bonusPercentage
          : null,
      bonusValue:
        values?.bonus.bonusValue !== 0 ? values?.bonus.bonusValue : null,
      bonusExpiryDate:
        !values?.bonus.bonusAvailableDays && values?.bonus.bonusExpiryDate
          ? new Date(values?.bonus.bonusExpiryDate)?.valueOf()
          : null,
      bonusAvailableDays:
        values?.bonus.bonusAvailableDays !== 0
          ? values?.bonus.bonusAvailableDays
          : null,
    }
  }

  return data
}
